'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { endSuperAdminServerSession } from '@/lib/superadmin/superAdminSession';

// Auth Types
export interface User {
  id: number;
  username: string;
  email: string;
  fullName: string;
  role: 'superadmin';
  permissions: string[];
  lastLoginAt?: string;
}


export interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export interface AuthContextType extends AuthState {
  // Authentication Actions
  loginAsSuperAdmin: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearSuperAdminSessionLocal: () => void;
  restoreSuperAdminUser: (user: User) => void;

  // Permission Helpers
  hasPermission: (permission: string) => boolean;
}

// Default Context Value
const defaultContextValue: AuthContextType = {
  user: null,
  isLoading: false,
  isAuthenticated: false,
  loginAsSuperAdmin: async () => false,
  logout: async () => {},
  clearSuperAdminSessionLocal: () => {},
  restoreSuperAdminUser: () => {},
  hasPermission: () => false,
};

// Create Context
const AuthContext = createContext<AuthContextType>(defaultContextValue);

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider Component
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Computed Properties
  const isAuthenticated = !!user;

  // Initialize Auth State
  useEffect(() => {
    initializeAuthState();
  }, []);

  // Initialize auth state from localStorage
  const initializeAuthState = async () => {
    try {
      setIsLoading(true);
      
      // Check localStorage for saved auth data
      const savedUser = localStorage.getItem('opas_user');
      
      if (savedUser) {
        const parsedUser: User = JSON.parse(savedUser);
        setUser(parsedUser);
        
      }
    } catch {
      // Clear corrupted data
      localStorage.removeItem('opas_user');
    } finally {
      setIsLoading(false);
    }
  };

  // SuperAdmin Login
  const loginAsSuperAdmin = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/opas/superadmin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      
      if (data.success && data.user) {
        const superAdminUser: User = {
          id: data.user.id,
          username: data.user.username,
          email: data.user.email,
          fullName: data.user.fullName,
          role: 'superadmin',
          permissions: data.user.permissions,
          lastLoginAt: data.user.lastLoginAt,
        };

        setUser(superAdminUser);

        // Eczane oturumu kalıntılarını temizle — SuperAdmin eczane bağlamında değildir
        localStorage.removeItem('tenantId');
        localStorage.removeItem('userId');
        localStorage.removeItem('userType');
        localStorage.removeItem('username');
        localStorage.removeItem('memberSince');
        localStorage.setItem('opas_user', JSON.stringify(superAdminUser));

        return true;
      }

      return false;
    } catch {
      return false;
    }
  };

  const clearSuperAdminSessionLocal = useCallback(() => {
    setUser(null);
    localStorage.removeItem('opas_user');
  }, []);

  const restoreSuperAdminUser = useCallback((nextUser: User) => {
    setUser(nextUser);
    localStorage.setItem('opas_user', JSON.stringify(nextUser));
  }, []);

  // Logout — tüm superadmin state'ini temizle (çerezler silinmeden yönlendirme yapma)
  const logout = useCallback(async () => {
    setUser(null);
    localStorage.removeItem('opas_user');
    await endSuperAdminServerSession();
    window.location.assign('/super-login');
  }, []);

  // Permission Check
  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    return user.permissions.includes(permission) || user.permissions.includes('GLOBAL_ACCESS');
  };

  // Context Value
  const contextValue: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    loginAsSuperAdmin,
    logout,
    clearSuperAdminSessionLocal,
    restoreSuperAdminUser,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom Hook
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
